import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import NewTitle from "../../extra/Title";
import Table from "../../extra/Table";
import Pagination from "../../extra/Pagination";
import UserAvatar from "../../extra/UserAvatar";
import { apiInstanceFetch } from "../../../util/api";

const ReferralPage = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("All");
  const [endDate, setEndDate] = useState("All");
  const [summary, setSummary] = useState({
    totalReferrals: 0,
    totalRewardCoins: 0,
    totalReferrerRewardCoins: 0,
    totalReferredRewardCoins: 0,
  });
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestVersion = useRef(0);

  const normalizeDateValue = (value) => {
    if (!value || value === "All") return "All";

    if (value instanceof Date) {
      return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "All";
    }

    if (typeof value === "string") {
      const normalizedString = value.trim();
      if (!normalizedString) return "All";

      const parsed = dayjs(
        normalizedString,
        ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"],
        true,
      );
      if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
      }

      return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "All";
    }

    return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "All";
  };

  const startDateData = normalizeDateValue(startDate);
  const endDateData = normalizeDateValue(endDate);

  const getRewardValue = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") {
      return Number(fallback || 0);
    }
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : Number(fallback || 0);
  };

  const renderUserInfo = (user) => {
    if (!user) return "-";

    const userId = user?._id;
    const displayName = user.fullName || user.nickName || user.email || "-";
    const email = user.email || "";

    const content = (
      <>
        <UserAvatar source={user.image} name={displayName} />
        <span className="text-start">
          <span className="d-block text-capitalize text-nowrap">
            {displayName}
          </span>
          {email && <small className="d-block text-muted">{email}</small>}
        </span>
      </>
    );

    if (!userId)
      return (
        <div className="referral-user-cell d-flex align-items-center justify-content-start gap-2 w-100">
          {content}
        </div>
      );

    return (
      <button
        type="button"
        className="referral-user-cell cursorPointer border-0 bg-transparent d-flex align-items-center justify-content-start gap-2 p-0 w-100 text-start"
        aria-label={`View ${displayName} profile`}
        onClick={() =>
          navigate("/admin/userProfile", { state: { id: userId } })
        }
      >
        {content}
      </button>
    );
  };

  const loadReferralData = async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError("");

    try {
      const [summaryRes, listRes] = await Promise.all([
        apiInstanceFetch.get(
          `admin/referral/summary?startDate=${startDateData}&endDate=${endDateData}`,
        ),
        apiInstanceFetch.get(
          `admin/referral/list?startDate=${startDateData}&endDate=${endDateData}`,
        ),
      ]);
      if (version !== requestVersion.current) return;
      if (summaryRes?.status === false || listRes?.status === false)
        throw new Error("Could not load referral earnings. Please refresh.");

      setSummary(
        summaryRes?.summary || {
          totalReferrals: 0,
          totalRewardCoins: 0,
          totalReferrerRewardCoins: 0,
          totalReferredRewardCoins: 0,
        },
      );
      setData(listRes?.data || []);
      setPage(1);
    } catch (error) {
      if (version !== requestVersion.current) return;
      console.error(error);
      setError("Could not load referral earnings. Please refresh.");
      setSummary({
        totalReferrals: 0,
        totalRewardCoins: 0,
        totalReferrerRewardCoins: 0,
        totalReferredRewardCoins: 0,
      });
      setData([]);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadReferralData();
    return () => {
      requestVersion.current++;
    };
  }, [startDateData, endDateData]);

  const columns = [
    {
      Header: "NO",
      body: "no",
      Cell: ({ index }) => <span>{(page - 1) * size + index + 1}</span>,
    },
    {
      Header: "REFERRER",
      body: "referrerUser",
      Cell: ({ row }) => renderUserInfo(row.referrerUser),
    },
    {
      Header: "REFERRER EARNED",
      body: "referrerRewardCoins",
      Cell: ({ row }) => (
        <span>{getRewardValue(row.referrerRewardCoins, row.coin)}</span>
      ),
    },
    {
      Header: "REFERRED USER",
      body: "referredUser",
      Cell: ({ row }) => renderUserInfo(row.referredUser),
    },
    {
      Header: "REFERRED EARNED",
      body: "referredRewardCoins",
      Cell: ({ row }) => <span>{getRewardValue(row.referredRewardCoins)}</span>,
    },
    {
      Header: "DATE",
      body: "referredDate",
      Cell: ({ row }) => (
        <span>
          {row.referredDate && dayjs(row.referredDate).isValid()
            ? dayjs(row.referredDate).format("DD/MM/YYYY")
            : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="userPage channelPage">
      <div className="dashboardHeader primeHeader mb-3 p-0">
        <NewTitle
          dayAnalyticsShow={true}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDate}
          endDate={endDate}
          titleShow={true}
          name="Referral"
        />
      </div>

      {error && (
        <div role="alert" className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="row g-2 mb-3 referralSummaryRow d-flex align-items-center justify-content-center">
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="dashBoxData bg-white p-2 rounded shadow-sm d-flex align-items-center justify-content-center text-center">
            <div className="dashBox-text w-100">
              <h5 className="text-center">Total Referrals</h5>
              <h6 className="text-center pt-1 fw-bold">
                {summary?.totalReferrals || 0}
              </h6>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="dashBoxData bg-white p-2 rounded shadow-sm d-flex align-items-center justify-content-center text-center">
            <div className="dashBox-text w-100">
              <h5 className="text-center">Referrer Reward Coins</h5>
              <h6 className="text-center pt-1 fw-bold">
                {summary?.totalReferrerRewardCoins || 0}
              </h6>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
          <div className="dashBoxData bg-white p-2 rounded shadow-sm d-flex align-items-center justify-content-center text-center">
            <div className="dashBox-text w-100">
              <h5 className="text-center">Referred Reward Coins</h5>
              <h6 className="text-center pt-1 fw-bold">
                {summary?.totalReferredRewardCoins || 0}
              </h6>
            </div>
          </div>
        </div>
      </div>

      <div className="user-table mb-1 referral-table">
        {loading ? (
          <div role="status" className="py-4 text-center text-muted">
            Loading...
          </div>
        ) : (
          <Table
            data={data.slice()}
            mapData={columns}
            PerPage={size}
            Page={page}
            type="client"
          />
        )}
        <Pagination
          type="server"
          activePage={page}
          rowsPerPage={size}
          userTotal={data.length}
          setPage={setPage}
          actionShow={false}
          handleRowsPerPage={(value) => {
            setPage(1);
            setSize(Number(value) || 20);
          }}
          handlePageChange={setPage}
        />
      </div>
    </div>
  );
};

export default ReferralPage;
